import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { closeCashRegister, closeCashRegisterBatch, getCashRegisterClosePreflight, getLastOpenCashRegister } from '../../../../services/CashRegisterService';
import { getPosAvailability } from '../../../../services/PosPaymentService';
import { batchMessage, batchSucceeded, posValuesFromBatch } from './batchClose';
import { useCurrencies } from '../../../../context/CurrencyContext';
import ForeignCurrencyCashForm, { buildCurrencyBalances } from './ForeignCurrencyCashForm';
import CashCurrencyStepper, { currencyHeading } from './CashCurrencyStepper';
import { DEFAULT_CURRENCY } from '../../../../utils/currency';

const CloseCashRegisterModal = ({ open, onClose, onSuccess, locationId, terminalId }) => {
  
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [discrepancy, setDiscrepancy] = useState(false);
    const [lastOpen, setLastOpen] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [stockCountStatus, setStockCountStatus] = useState(null);
    // Paso 2 (solo si el tenant tiene otras monedas habilitadas): efectivo y ventas en cada moneda distinta de pesos.
    const { currencies, multiCurrency } = useCurrencies();
    const foreignCurrencies = currencies.filter((c) => c.code !== DEFAULT_CURRENCY);
    const [step, setStep] = useState(0);
    const [foreignValues, setForeignValues] = useState({});
    // Terminal con POS integrado: el campo "POS" no se escribe, lo completa el cierre del lote (el total que informa el POS).
    const [posIntegrated, setPosIntegrated] = useState(false);
    const [batch, setBatch] = useState(null);
    const [batchIssue, setBatchIssue] = useState(null);
    const [batchLoading, setBatchLoading] = useState(false);
    const [skipBatch, setSkipBatch] = useState(false);
    const fieldLabels = {
        mercadoPago: "MercadoPago",
        pos: "POS",
        pedidosYa: "PedidosYa",
    };
    const [formData, setFormData] = useState({
        mercadoPago: '',
        pos: '',
        pedidosYa: '',
        dec2000: '',
        dec1000: '',
        dec500: '',
        dec200: '',
        dec100: '',
        dec50: '',
        dec20: '',
        dec10: '',
        dec5: '',
        dec2: '',
        dec1: '',
        notes: '',
    });

    const [fieldErrors, setFieldErrors] = useState({});

    // 🔹 Obtiene la última caja abierta cuando se abre el modal
    useEffect(() => {
        if (open) {
            setLoading(true);
            setAttempts(0);
            setStep(0);
            setForeignValues({});
            setStockCountStatus(null);
            setBatch(null);
            setBatchIssue(null);
            setSkipBatch(false);
            setPosIntegrated(false);
            getPosAvailability(locationId, terminalId).then((availability) => setPosIntegrated(availability.enabled && availability.provider?.supportsBatchClose !== false)).catch(() => setPosIntegrated(false));
            getLastOpenCashRegister(locationId, terminalId)
                .then((data) => {
                    if (data) {
                        setLastOpen(data);
                    }
                })
                .catch((err) => console.error("Error obteniendo última apertura:", err))
                .finally(() => setLoading(false));
            getCashRegisterClosePreflight(locationId)
                .then(setStockCountStatus)
                .catch((err) => console.error("Error verificando conteo de stock pendiente:", err));
        }
    }, [open, locationId, terminalId]);

    const stockCountBlocked = ['NOT_STARTED', 'PENDING'].includes(stockCountStatus?.type);
    const stockCountWarning = stockCountStatus?.type === 'NOT_STARTED'
        ? 'Hoy corresponde un conteo de stock y todavía no fue iniciado. Completalo antes de cerrar la caja.'
        : stockCountStatus?.type === 'PENDING'
            ? `Quedan ${stockCountStatus.pendingCount} ítem(s) sin resolver en el conteo de stock de hoy. Completalo antes de cerrar la caja.`
            : null;

    // 🔹 Maneja cambios en los inputs
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });

        // Si el usuario cambia un valor, eliminamos la marca de error en ese campo
        setFieldErrors(prevErrors => ({ ...prevErrors, [e.target.name]: false }));
    };

    // 🔹 Calcula la suma total de billetes ingresados en el cierre
    const calculateDeclaredCash = () => {
        return (parseInt(formData.dec2000) || 0) * 2000 +
               (parseInt(formData.dec1000) || 0) * 1000 +
               (parseInt(formData.dec500) || 0) * 500 +
               (parseInt(formData.dec200) || 0) * 200 +
               (parseInt(formData.dec100) || 0) * 100 +
               (parseInt(formData.dec50) || 0) * 50 +
               (parseInt(formData.dec20) || 0) * 20 +
               (parseInt(formData.dec10) || 0) * 10 +
               (parseInt(formData.dec5) || 0) * 5 +
               (parseInt(formData.dec2) || 0) * 2 +
               (parseInt(formData.dec1) || 0) * 1;
    };

    const calculateExpectedCash = () => {
      return (parseInt(lastOpen.dec2000) || 0) * 2000 +
             (parseInt(lastOpen.dec1000) || 0) * 1000 +
             (parseInt(lastOpen.dec500) || 0) * 500 +
             (parseInt(lastOpen.dec200) || 0) * 200 +
             (parseInt(lastOpen.dec100) || 0) * 100 +
             (parseInt(lastOpen.dec50) || 0) * 50 +
             (parseInt(lastOpen.dec20) || 0) * 20 +
             (parseInt(lastOpen.dec10) || 0) * 10 +
             (parseInt(lastOpen.dec5) || 0) * 5 +
             (parseInt(lastOpen.dec2) || 0) * 2 +
             (parseInt(lastOpen.dec1) || 0) * 1;
  };  

    // 🔹 Valida discrepancia solo en el primer intento
    const validateDiscrepancy = () => {
        if (!lastOpen || attempts > 0) return false;

        // 🔹 **Calculamos el total esperado** (última apertura)
        const expectedTotal = 
          lastOpen.mercadoPago + 
          lastOpen.efectivo + 
          lastOpen.pos + 
          lastOpen.pedidosYa + 
          calculateExpectedCash()

        // 🔹 **Calculamos el total declarado** (formulario actual)
        const declaredTotal = 
          (parseFloat(formData.mercadoPago) || 0) +
          (parseFloat(formData.pos) || 0) +
          (parseFloat(formData.pedidosYa) || 0) +
          calculateDeclaredCash();

          let hasDiscrepancy = false;
          const newErrors = {};
      
          // **🔹 Revisamos cada billete/moneda individualmente**
          Object.keys(formData).forEach((key) => {
              if (key === 'notes') return;
              // El POS no se cuenta: lo informa el lote. Su diferencia con el sistema se muestra aparte, no como "volvé a contar".
              if (key === 'pos' && posIntegrated) return;
              const declaredValue = parseInt(formData[key]) || 0;
              const expectedValue = parseInt(lastOpen[key]) || 0;
      
              if (declaredValue !== expectedValue) {
                  newErrors[key] = true; // 🔹 Marcar en rojo
                  hasDiscrepancy = true;
              }
          });
        
          setFieldErrors(newErrors);
          setDiscrepancy(hasDiscrepancy);
        
        return hasDiscrepancy;
    };

    // Pide el cierre del lote al POS (puede tardar unos segundos). Nunca lanza: un fallo vuelve como ERROR para que el cajero decida.
    const runBatchClose = async () => {
        setBatchLoading(true);
        try {
            return await closeCashRegisterBatch({ location_id: parseInt(locationId), terminal_code: terminalId });
        } catch (err) {
            return { status: 'ERROR', message: err?.response?.data?.error_message || 'No hay comunicación con el servidor.' };
        } finally {
            setBatchLoading(false);
        }
    };

    // opts.skip: cerrar la caja sin poder cerrar el lote del POS (queda pendiente y entra en el próximo cierre).
    const handleSubmit = async (opts = {}) => {
        setError(false);
        setDiscrepancy(false);
        const skip = opts.skip ?? skipBatch;
        // El campo POS lo completa el lote (o, si se cierra sin lote, lo que el sistema tenía anotado).
        const posPending = posIntegrated && !batch && !skip;
        // El campo POS no lo escribe el cajero: lo completa el lote o, si se cierra sin lote, lo que el sistema tenía anotado.
        const posFilledByTheSystem = posIntegrated && (posPending || skip);

        const allFieldsFilled = Object.entries(formData)
            .filter(([key]) => key !== 'notes' && !(key === 'pos' && posFilledByTheSystem))
            .every(([, value]) => value !== '');
        if (!allFieldsFilled) {
            setError(true);
            setFieldErrors(
                Object.keys(formData).reduce((acc, key) => {
                    if (key !== 'notes' && !(key === 'pos' && posFilledByTheSystem) && formData[key] === '') acc[key] = true; // Marcar en rojo si está vacío
                    return acc;
                }, {})
            );
            return;
        }

        let posValue = parseFloat(formData.pos) || 0;
        let foreignToSend = foreignValues;
        if (posIntegrated && skip) {
            // Sin lote: se declara lo que el sistema tenía anotado, así no se inventa una diferencia.
            posValue = lastOpen?.pos || 0;
            foreignToSend = { ...foreignValues };
            foreignCurrencies.forEach(({ code }) => {
                const systemPos = lastOpen?.balances?.find((balance) => balance.currency === code)?.pos ?? 0;
                foreignToSend[code] = { ...(foreignToSend[code] || {}), pos: String(systemPos) };
            });
            setSkipBatch(true);
            setBatchIssue(null);
            setFormData((prev) => ({ ...prev, pos: String(posValue) }));
            setForeignValues(foreignToSend);
        } else if (posPending) {
            const result = await runBatchClose();
            if (result?.status === 'NOT_CONFIGURED') {
                // La terminal no tiene POS integrado después de todo: el campo se completa a mano como siempre.
                setPosIntegrated(false);
                setFieldErrors({ pos: true });
                setError(true);
                return;
            }
            if (!batchSucceeded(result)) {
                setBatchIssue(result);
                return;
            }
            const values = posValuesFromBatch(result);
            posValue = values.pesos;
            foreignToSend = { ...foreignValues };
            foreignCurrencies.forEach(({ code }) => {
                foreignToSend[code] = { ...(foreignToSend[code] || {}), pos: String(values.foreign[code] ?? 0) };
            });
            setBatch(result);
            setBatchIssue(null);
            setFormData((prev) => ({ ...prev, pos: String(values.pesos) }));
            setForeignValues(foreignToSend);
        }

        // 🔹 Si es el primer intento, validar discrepancias
        if (attempts === 0) {
            const hasDiscrepancy = validateDiscrepancy();
            setDiscrepancy(hasDiscrepancy);

            if (hasDiscrepancy) {
                setAttempts(1); // Incrementamos el contador de intentos
                return; // Bloquea el envío solo la primera vez
            }
        }

        if (multiCurrency && step < foreignCurrencies.length) {
            setStep(step + 1);
            return;
        }

        setLoading(true);

        try {
            await closeCashRegister({
                location_id: parseInt(locationId),
                terminal_code: terminalId,
                mercado_pago: parseFloat(formData.mercadoPago) || 0,
                pos: posValue,
                pedidos_ya: parseFloat(formData.pedidosYa) || 0, 
                type: 2, // 2 representa cierre
                dec_2000: parseInt(formData.dec2000) || 0,
                dec_1000: parseInt(formData.dec1000) || 0,
                dec_500: parseInt(formData.dec500) || 0,
                dec_200: parseInt(formData.dec200) || 0,
                dec_100: parseInt(formData.dec100) || 0,
                dec_50: parseInt(formData.dec50) || 0,
                dec_20: parseInt(formData.dec20) || 0,
                dec_10: parseInt(formData.dec10) || 0,
                dec_5: parseInt(formData.dec5) || 0,
                dec_2: parseInt(formData.dec2) || 0,
                dec_1: parseInt(formData.dec1) || 0,
                notes: formData.notes?.trim() || null,
                ...(multiCurrency ? { currency_balances: buildCurrencyBalances(foreignCurrencies, foreignToSend) } : {}),
                // El lote del POS ya se cerró antes (o se eligió no cerrarlo): el backend no lo vuelve a cerrar.
                ...(posIntegrated ? { batch_handled: true, batch_skipped: skip } : {})
            });

            onClose();
            onSuccess();
        } catch (error) {
            console.error('Error cerrando la caja:', error);
            const message = error?.response?.data?.error_message || "Error al cerrar la caja";
            alert(message);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => {}} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>{t('close_cash_register')}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                {stockCountWarning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {stockCountWarning}
                    </Alert>
                )}
                {discrepancy && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {t('discrepancy_warning')}
                    </Typography>
                )}
                {batchLoading && (
                    <Box data-testid="batch-closing" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <CircularProgress size={24} />
                        <Box>
                            <Typography variant="subtitle2">Cerrando el lote en el POS…</Typography>
                            <Typography variant="body2" color="text.secondary">Puede tardar unos segundos. No cierres esta ventana.</Typography>
                        </Box>
                    </Box>
                )}
                {[batch, batchIssue].map((result, index) => {
                    const info = result ? batchMessage(result) : null;
                    return info ? (
                        <Alert key={index === 0 ? 'batch' : 'batch-issue'} data-testid={index === 0 ? 'batch-result' : 'batch-issue'} severity={info.severity} sx={{ mb: 2 }}>
                            <AlertTitle>{info.title}</AlertTitle>
                            {info.message}
                        </Alert>
                    ) : null;
                })}
                {multiCurrency && <CashCurrencyStepper foreignCurrencies={foreignCurrencies} step={step} />}
                {step > 0 && <ForeignCurrencyCashForm currency={foreignCurrencies[step - 1]} values={foreignValues} onChange={setForeignValues} showSales lockPos={posIntegrated} />}
                {step === 0 && multiCurrency && <Typography variant="h6" sx={{ mb: 0.5 }}>{currencyHeading(DEFAULT_CURRENCY)}</Typography>}
                {step === 0 && multiCurrency && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Declará lo cobrado en pesos y contá los billetes y monedas. Las otras monedas van en el paso siguiente.</Typography>}
                {step === 0 && <Typography variant="subtitle2" sx={{ mb: 1 }}>Efectivo en caja: billetes y monedas</Typography>}
                {step === 0 && <Grid container spacing={1.25}>
                    {["dec2000", "dec1000", "dec500", "dec200", "dec100", "dec50", "dec20", "dec10", "dec5", "dec2", "dec1"].map((field) => (
                        <Grid item xs={6} sm={4} key={field}>
                            <TextField
                                label={field.replace("dec", "Cantidad de $")}
                                name={field}
                                fullWidth
                                size="small"
                                onChange={handleChange}
                                type="number"
                                inputProps={{ min: 0 }}
                                error={fieldErrors[field]}
                                helperText={fieldErrors[field] ? t('count_again') : ''}                            
                            />
                        </Grid>
                    ))}
                </Grid>}
                {step === 0 && <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>Cobrado por otros medios</Typography>}
                {step === 0 && <Grid container spacing={1.25}>
                    {["mercadoPago", "pos", "pedidosYa"].map((field) => (
                        <Grid item xs={4} key={field}>
                            <TextField
                                label={fieldLabels[field]} 
                                name={field}
                                fullWidth
                                size="small"
                                value={formData[field]}
                                onChange={handleChange}
                                type="number"
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                inputProps={{ min: 0, 'data-testid': `close-${field}` }}
                                disabled={field === 'pos' && posIntegrated}
                                placeholder={field === 'pos' && posIntegrated ? 'Lo completa el POS' : undefined}
                                error={fieldErrors[field]}
                                helperText={field === 'pos' && posIntegrated
                                    ? (batch ? 'Total informado por el POS' : skipBatch ? 'Lo registrado por el sistema (lote pendiente)' : 'Se completa al cerrar el lote')
                                    : (fieldErrors[field] ? t('count_again') : '')}
                            />
                        </Grid>
                    ))}
                </Grid>}
                {step === 0 && <TextField
                    sx={{ mt: 1.5 }}
                    label={t('notes')}
                    name="notes"
                    fullWidth
                    multiline
                    minRows={2}
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder={t('cash_closing_notes_placeholder')}
                />}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {step > 0 ? <Button onClick={() => setStep(step - 1)} disabled={batchLoading}>Atrás</Button> : <Button onClick={onClose} disabled={batchLoading}>{t('cancel')}</Button>}
                {batchIssue && <Button onClick={() => handleSubmit({ skip: true })} disabled={loading || batchLoading}>Cerrar la caja igual</Button>}
                <LoadingButton onClick={() => handleSubmit()} loading={loading || batchLoading} disabled={stockCountBlocked} variant="contained" color="primary">
                    {batchIssue ? 'Reintentar cierre del lote' : multiCurrency && step < foreignCurrencies.length ? 'Siguiente' : t('confirm')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CloseCashRegisterModal;
